import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { propGeometry, propMaterials } from './props.js';
import { WINDUP_REACH, clipsFor } from './clips.js';
import { clothMaterial, dressPieces, measureBody } from './dress.js';

/**
 * What a person is made of, and how they move. The only file that knows.
 *
 * One character for everybody (`public/models/figure.glb`, built by
 * `assets-src/build_character.py` from the Mixamo downloads in
 * `assets-src/mixamo/`): a skinned mesh on Mixamo's skeleton, and every clip on
 * it as a named animation. It is loaded once; each person gets a clone that
 * shares the geometry and the clips and has its own bones and its own
 * `AnimationMixer`.
 *
 * Which clip plays is `clips.js`, from the state `gait.js` reads off the body.
 * How it plays is here:
 *
 *   crossfades   every clip's weight moves toward where it is wanted over a
 *                fifth of a second, so nothing snaps
 *   two layers   each clip is split in two at load, the legs and hips and the
 *                rest, so throwing can be laid over whatever the legs are doing
 *   carrying     no clip: the arms are held over the idle or the walk (HOLDS),
 *                and a one-handed thing is drawn in the right hand
 *   no sliding   the clips that walk play at the body's speed over the speed
 *                the clip covers ground at, which the build measured off the
 *                planted foot and wrote into the file
 *   the throw    held at the wind-up while the key is, and played through when
 *                it is let go
 *
 * Told apart by material only, as before: four regions (cloth, skin, hair,
 * shoes) over one brightness-only detail map, each tinted per person. Cloth is
 * what you have got on; skin and hair come from a small palette by a key that
 * is the same every time for the same person.
 *
 * It reads a plain object of numbers and writes nothing but its own bones.
 */

/** Skin tones, light to dark, as tints over a brightness map. */
export const SKIN = Object.freeze([0xf3cfb0, 0xe2b089, 0xc98f67, 0xa06c4a, 0x7a4b31, 0x55331f]);

/** Hair colours: black, dark brown, brown, dark blond, grey. */
export const HAIR = Object.freeze([0x1c1916, 0x3a291d, 0x684528, 0x9a7646, 0x8a8682]);

/** Shoes are shoes whoever is in them. */
const SHOE = 0x1b1b1e;

/** The file, served next to the page. Relative, like everything the build emits. */
const MODEL = 'models/figure.glb';

/** Seconds for a clip to come fully in or go fully out. */
export const FADE = 0.22;

/**
 * What the hash is mixed with. Chosen so the five presets on the hiring screen
 * come out as five different people; `figure.test.js` holds it to that. (This
 * layer may not read the presets itself: it reads nothing from the game.)
 */
const LOOK_SALT = 'look3';

function hashOf(key) {
  const text = String(key ?? '');
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** The skin and hair a key picks. */
export function lookFor(key) {
  const h = hashOf(`${LOOK_SALT}${key}`);
  return { skin: SKIN[h % SKIN.length], hair: HAIR[Math.floor(h / SKIN.length) % HAIR.length] };
}

/** A Mixamo bone's own name, without whatever numbered prefix the download carried. */
export function bareBone(name) {
  return String(name).replace(/^mixamorig\d*:?/, '');
}

/** The legs and the hips: the lower layer. Everything else is the upper. */
const LOWER = /^(Hips|(Left|Right)(UpLeg|Leg|Foot|ToeBase|Toe_End))$/;

/**
 * How far above a seat a sitting body's hip joint is, in metres: about the
 * thickness of a thigh. A seated body is placed at the seat, and the sitting
 * clip holds the hips at chair height over the feet, so the body goes down by
 * the difference while it sits.
 */
const ON_SEAT = 0.1;

/**
 * Carrying, as arms held over the base clip rather than a clip of its own.
 *
 * Each hold is where the arm points in the body's own frame (+Z the way the
 * body faces, +X its left, +Y up), for the upper arm and the forearm; left
 * first, the right is the mirror. They are turned into a fixed rotation for
 * each bone against its parent once, from the bind pose, when the body is
 * put on, and every frame the bone is blended toward that. Fixed against the
 * parent rather than aimed in the world, so nothing is recomputed from where
 * the clip happened to leave the arm: the roll along the arm is the bind's,
 * the wrist is held straight, and a frame is the same as the frame before.
 *
 * `swing` is how much of the clip's own upper arm is let through: none for a
 * round against the chest, a fifth for one hand, so a walking arm with a thing
 * in its hand still moves with the stride instead of fighting it.
 *
 *   two   a round, a stack: both upper arms down and a little forward with the
 *         elbows in at the sides, both forearms level in front and turned in
 *         toward each other, which is how a thing is held against the body
 *   one   anything of a hand's size: the right upper arm a little forward, the
 *         forearm level and out in front, the item in the hand at the height
 *         of the elbow and clear of the hips (it was lower, and a thing held
 *         at the fly read as exactly that); the left arm is the clip's.
 *         Anything that fits a pocket is not held at all: the game says so
 *         (`pocket` on its catalogue row) and hands the body no hold
 */
const HOLDS = Object.freeze({
  two: { upper: [0.14, -0.93, 0.34], fore: [-0.06, 0.04, 1], sides: ['Left', 'Right'], swing: 0 },
  one: { upper: [0.1, -0.95, 0.3], fore: [0.14, 0.18, 0.97], sides: ['Right'], swing: 0.2 },
});

/**
 * Where a held thing sits in the hand, by its shape, in the hand's own grip
 * frame (fingers, palm, thumb, measured off the bind: the fingers are the line
 * of the forearm through the wrist, the palm faces the floor in the T).
 *
 *   flat    height under half its width (a hand mirror, a card, a coaster):
 *           lying against the palm, its face across the fingers
 *   tall    anything else upright (a bottle, a shaker, a candle, a broom):
 *           gripped in the fist, standing along the thumb
 *   round   a sphere: sitting in the palm
 */
const GRIP = Object.freeze({ reach: 0.055, gap: 0.012 });

/** Clips that play once and hold, rather than loop. */
const ONCE = new Set(['throw', 'knock']);

/**
 * Stepping in place while turning on the spot, instead of spinning on a
 * plinth. Turning faster than `from` rad/s brings the walk in, up to `most` of
 * the legs at `full`; the cycle turns `cycle` times per radian, so a step for
 * about every forty-five degrees.
 */
const PIVOT = Object.freeze({ from: 0.9, full: 4, most: 0.7, cycle: 0.35 });

/** Cycles a second a stopping stride is at least played on at, to finish its step. */
const SETTLE = 1.4;

/**
 * Leaning, in radians: forward or back by `perAccel` per m/s² of the body's
 * change of speed (eased at `accelEase` a second), at most `most`; banked by
 * `perTurn` per (rad/s × m/s) of turning at speed, at most `bank`; and both
 * eased at `ease` a second. Ours, by eye in the close-ups.
 */
const LEAN = Object.freeze({ perAccel: 0.006, accelEase: 10, most: 0.07, perTurn: 0.022, bank: 0.12, ease: 7 });

/** The next phase at which the feet pass, from `phase` onward. */
function nextRest(phase, rest = [0.25, 0.75]) {
  const ahead = rest.map((r) => (r - phase + 1) % 1);
  const i = ahead[0] <= ahead[1] ? 0 : 1;
  return rest[i];
}

/*
 * The file, loaded once for the whole roster: the character, every clip split
 * into its two layers, and the speeds the walking clips cover ground at.
 * `waiting` is every figure dressed before it arrived.
 */
let asset = null;
let loading = null;
const waiting = new Set();

function canLoad() {
  return typeof window !== 'undefined' && typeof document !== 'undefined' && typeof fetch === 'function';
}

function layers(clip) {
  const lower = [];
  const upper = [];
  for (const track of clip.tracks) {
    const node = bareBone(track.name.split('.')[0]);
    (LOWER.test(node) ? lower : upper).push(track);
  }
  return {
    lower: new THREE.AnimationClip(`${clip.name}:lower`, clip.duration, lower),
    upper: new THREE.AnimationClip(`${clip.name}:upper`, clip.duration, upper),
  };
}

/** Every bone's bind translation and scale, by its bare name. */
function restOf(scene) {
  const rest = {};
  scene.traverse((node) => {
    if (node.isBone) rest[bareBone(node.name)] = { position: node.position.clone(), scale: node.scale.clone() };
  });
  return rest;
}

/**
 * Takes out of a clip the tracks that only hold a bone where its bind already
 * has it: every scale track that never leaves the bind's scale, and every
 * translation below the hips that never leaves the bind's. Mixamo keys all
 * three channels on every bone, and a track that writes a constant still
 * writes, which would put a build (`shape`) back to the bind on the first
 * frame. The pose a clip plays is the same with or without them.
 */
export function unpin(clip, rest) {
  clip.tracks = clip.tracks.filter((track) => {
    const [node, channel] = track.name.split('.');
    const bone = bareBone(node);
    if (channel !== 'scale' && (channel !== 'position' || bone === 'Hips')) return true;
    const bind = rest[bone]?.[channel];
    if (!bind) return true;
    const v = track.values;
    for (let i = 0; i < v.length; i += 3) {
      if (Math.abs(v[i] - bind.x) > 1e-4 || Math.abs(v[i + 1] - bind.y) > 1e-4 || Math.abs(v[i + 2] - bind.z) > 1e-4) return true;
    }
    return false;
  });
  return clip;
}

function load() {
  if (loading || !canLoad()) return loading;
  const url = new URL(MODEL, document.baseURI).href;
  loading = new GLTFLoader()
    .loadAsync(url)
    .then((gltf) => {
      const clips = {};
      const rest = restOf(gltf.scene);
      for (const clip of gltf.animations) {
        if (TAME[clip.name] !== undefined) tame(clip, TAME[clip.name]);
        unpin(clip, rest);
      }
      let speeds = {};
      try {
        speeds = JSON.parse(gltf.scene.userData?.clipSpeeds ?? gltf.userData?.clipSpeeds ?? '{}');
      } catch {
        speeds = {};
      }
      const gaits = study(gltf.scene, gltf.animations, speeds);
      for (const clip of gltf.animations) clips[clip.name] = layers(clip);
      const sitHips = Number(gltf.scene.userData?.sitHips ?? 0);
      const parts = [];
      gltf.scene.traverse((node) => {
        if (node.isSkinnedMesh) parts.push({ region: String(node.material?.name ?? '').replace('figure_', '') || 'cloth', geometry: node.geometry });
      });
      asset = { scene: gltf.scene, clips, speeds, gaits, sitHips, body: measureBody(parts) };
      for (const figure of waiting) figure.wear();
      waiting.clear();
      return asset;
    })
    .catch((error) => {
      console.warn(`[figure] no body: ${error.message}`);
      return null;
    });
  return loading;
}

/**
 * How much of each walking clip's pelvis to keep, by clip.
 *
 * D: "the walk looks wrong, too much hip sway". Measured, peak to peak over a
 * cycle, the Mixamo walk rolls the pelvis 10.7 degrees and twists it 14.4; a
 * relaxed walk is nearer 7 and 9. The run's 11.6 and 16.2 are a run's, and are
 * only taken down a little. See `tame`.
 */
const TAME = Object.freeze({ walk: 0.6, run: 0.85 });

/** The bones above the pelvis that go with it, so the shoulders do not over-counter. */
const SPINE = ['Spine', 'Spine1', 'Spine2'];

/**
 * Pulls a clip's pelvis toward its own average, keeping `keep` of the sway.
 *
 * Done to the clip once, at load, rather than to the bones every frame, so
 * every body that plays it plays the same quieter walk at no cost. Three parts:
 *
 *   the pelvis   each key's rotation slerped toward the clip's mean by `keep`,
 *                which scales the roll, the twist and the pitch together
 *   the spine    the same, so the chest is not left counter-rotating against a
 *                pelvis that no longer turns as far (which reads as the
 *                shoulders swinging more, not less)
 *   the thighs   counter-rotated by exactly what the pelvis lost, so each leg
 *                points where it pointed before and every foot lands where the
 *                clip put it: nothing slides, and the build's measured speed
 *                for the clip still holds
 *
 * The side-to-side travel of the hips is left alone: taking it out moves the
 * planted foot with it, and 5 cm is a normal walk's.
 */
export function tame(clip, keep) {
  const track = (bone) =>
    clip.tracks.findIndex((t) => t.name.endsWith('.quaternion') && bareBone(t.name.split('.')[0]) === bone);
  const at = track('Hips');
  if (at < 0) return false;
  const hips = clip.tracks[at];
  const times = hips.times;
  const n = times.length;
  const read = (index) => {
    const t = clip.tracks[index];
    const out = [];
    const same = t.times.length === n && t.times.every((v, i) => Math.abs(v - times[i]) < 1e-6);
    const interpolant = same ? null : t.createInterpolant();
    for (let i = 0; i < n; i += 1) {
      const q = new THREE.Quaternion();
      if (same) q.fromArray(t.values, i * 4);
      else q.fromArray(interpolant.evaluate(times[i]));
      out.push(q.normalize());
    }
    return out;
  };
  const mean = (list) => {
    const m = new THREE.Vector4();
    for (const q of list) {
      const sign = q.dot(list[0]) < 0 ? -1 : 1;
      m.x += q.x * sign;
      m.y += q.y * sign;
      m.z += q.z * sign;
      m.w += q.w * sign;
    }
    return new THREE.Quaternion(m.x, m.y, m.z, m.w).normalize();
  };
  const write = (index, list) => {
    const values = new Float32Array(n * 4);
    list.forEach((q, i) => q.toArray(values, i * 4));
    clip.tracks[index] = new THREE.QuaternionKeyframeTrack(clip.tracks[index].name, Float32Array.from(times), values);
  };

  const was = read(at);
  const centre = mean(was);
  const now = was.map((q) => centre.clone().slerp(q, keep));
  write(at, now);
  for (const side of ['Left', 'Right']) {
    const thigh = track(`${side}UpLeg`);
    if (thigh < 0) continue;
    const legs = read(thigh);
    write(thigh, legs.map((q, i) => now[i].clone().invert().multiply(was[i]).multiply(q)));
  }
  for (const bone of SPINE) {
    const index = track(bone);
    if (index < 0) continue;
    const list = read(index);
    const middle = mean(list);
    write(index, list.map((q) => middle.clone().slerp(q, keep)));
  }
  return true;
}

/**
 * When each striding clip puts its feet down, measured off the clip at load.
 *
 * A walk and a run of different lengths can only be blended if they are in
 * step, so each is given an `offset`: the share of the way through the clip at
 * which the left heel strikes (the left foot reaching the floor). Both
 * then play from one phase, 0 being that strike. `rest` is the two phases at
 * which the feet pass each other, which is where a stride begins from standing
 * and where a stopping body settles. `stride` is the ground a whole cycle
 * covers at 1x. Only walk and run are blended; the push is not.
 */
function study(scene, animations, speeds) {
  const gaits = {};
  const body = cloneSkinned(scene);
  const bones = {};
  body.traverse((node) => {
    if (node.isBone) bones[bareBone(node.name)] = node;
  });
  if (!bones.Hips || !bones.LeftFoot || !bones.RightFoot) return gaits;
  const mixer = new THREE.AnimationMixer(body);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const h = new THREE.Vector3();
  for (const name of ['walk', 'run']) {
    const clip = animations.find((c) => c.name === name);
    if (!clip || !(speeds[name] > 0)) continue;
    const action = mixer.clipAction(clip);
    action.play();
    const N = 120;
    const ahead = [];
    for (let i = 0; i < N; i += 1) {
      action.time = (i / N) * clip.duration;
      mixer.update(0);
      body.updateMatrixWorld(true);
      bones.Hips.getWorldPosition(h);
      bones.LeftFoot.getWorldPosition(a);
      bones.RightFoot.getWorldPosition(b);
      ahead.push({ left: a.z - h.z, low: a.y, gap: a.z - b.z });
    }
    action.stop();
    mixer.uncacheAction(clip);
    /*
     * The strike is the left foot coming down: the first sample at the bottom
     * of its travel after one above it. Not the foot furthest forward, which
     * in a walk is the same moment and in a run is mid-swing, a tenth of a
     * cycle before the foot lands, so the two clips blended disagreed about
     * when a foot was on the floor.
     */
    const floor = Math.min(...ahead.map((s) => s.low));
    const top = Math.max(...ahead.map((s) => s.low));
    const down = floor + Math.min(0.02, (top - floor) * 0.12);
    let strike = -1;
    for (let i = 0; i < N; i += 1) {
      if (ahead[i].low <= down && ahead[(i - 1 + N) % N].low > down) {
        strike = i;
        break;
      }
    }
    if (strike < 0) {
      strike = 0;
      for (let i = 1; i < N; i += 1) if (ahead[i].left > ahead[strike].left) strike = i;
    }
    const offset = strike / N;
    // The feet passing: where the gap between them changes sign, in phase from the strike.
    const rest = [];
    for (let k = 1; k <= N; k += 1) {
      const i = (strike + k) % N;
      const j = (strike + k - 1) % N;
      if (Math.sign(ahead[i].gap) !== Math.sign(ahead[j].gap)) rest.push(k / N);
    }
    gaits[name] = {
      speed: speeds[name],
      stride: speeds[name] * clip.duration,
      duration: clip.duration,
      offset,
      rest: rest.length >= 2 ? rest.slice(0, 2) : [0.25, 0.75],
    };
  }
  mixer.stopAllAction();
  return gaits;
}

/** Starts the file coming, for a check that wants to wait for it. */
export function figureReady() {
  return load() ?? Promise.resolve(null);
}

/** The clips in the file and the speeds it was built with, once it is here. */
export function figureAsset() {
  return asset ? { clips: Object.keys(asset.clips), speeds: { ...asset.speeds }, gaits: JSON.parse(JSON.stringify(asset.gaits)) } : null;
}

/**
 * A person: the body, once the file is here, and the setters the rest of the
 * game already used on it.
 */
export function dressFigure({ height = 1.8, colour = 0x3c4452, accent = 0xe8e4dc, look = null, style = null } = {}) {
  /*
   * Each person stands in their own time. One idle clip for everybody, but
   * played a little faster or slower and from a different moment of it per
   * person, off the same stable key as their skin and hair, so a row of crew
   * at the pass do not breathe and shift their weight as one. Standing and
   * talking only: a walk's speed is the ground it covers, and that is not
   * this layer's to vary.
   */
  const personal = hashOf(`idle${look ?? ''}`);
  const idleRate = 0.84 + (personal % 33) / 100;
  const idlePhase = ((personal >>> 8) % 1000) / 1000;
  const root = new THREE.Group();
  root.name = 'figure';
  const disposables = [];
  const keep = (thing) => {
    disposables.push(thing);
    return thing;
  };

  const tint = { cloth: new THREE.Color(colour), skin: new THREE.Color(), hair: new THREE.Color() };
  let materials = null;
  function setLook(key) {
    const picked = lookFor(key);
    tint.skin.set(picked.skin);
    tint.hair.set(picked.hair);
    if (materials) {
      materials.skin.color.copy(tint.skin);
      materials.hair.color.copy(tint.hair);
    }
  }
  setLook(look ?? accent);

  let body = null;
  let mixer = null;
  const meshes = [];
  const bones = {};
  /** Per clip, its two actions and where each one's weight has got to. */
  const actions = {};
  let released = false;
  let lastWindup = 0;
  /** How far each hold is in, faded like a clip. */
  const holding = { one: 0, two: 0 };
  /** What is in the right hand, drawn, and what it was last drawn as. */
  let item = null;
  let itemShape = null;
  let itemKey = null;
  let playing = { base: 'idle', upper: null, timeScale: 1 };
  /*
   * Walking, as one cycle: the phase the walk and the run both play from (0 is
   * the left heel striking), the cadence it last turned at, and the lean.
   */
  let phase = 0;
  let lastCycle = 0;
  let lean = 0;
  let bank = 0;
  let lastSpeed = 0;
  let accel = 0;

  function actionsFor(name) {
    if (actions[name]) return actions[name];
    const clip = asset.clips[name];
    if (!clip) return null;
    const make = (layer) => {
      const action = mixer.clipAction(clip[layer]);
      if (ONCE.has(name)) {
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
      }
      return { action, weight: 0 };
    };
    actions[name] = { lower: make('lower'), upper: make('upper') };
    return actions[name];
  }

  /** Moves one action's weight toward what is wanted of it, starting and stopping it. */
  function fade(slot, want, dt, startAt = null) {
    const step = FADE > 0 ? dt / FADE : 1;
    const before = slot.weight;
    slot.weight = want > before ? Math.min(want, before + step) : Math.max(want, before - step);
    const action = slot.action;
    if (slot.weight > 0 && before === 0) {
      action.reset();
      if (startAt !== null) action.time = startAt;
      action.play();
    }
    if (slot.weight <= 0) {
      if (action.isRunning()) action.stop();
      slot.weight = 0;
      return;
    }
    action.setEffectiveWeight(slot.weight);
  }

  const scratch = {
    a: new THREE.Vector3(),
    b: new THREE.Vector3(),
    d: new THREE.Vector3(),
    q: new THREE.Quaternion(),
    p: new THREE.Quaternion(),
    full: new THREE.Quaternion(),
    root: new THREE.Quaternion(),
    basis: new THREE.Matrix4(),
  };
  /** Per hold, per bone, the rotation against its parent it is held at. */
  const holdTargets = {};
  /** The right hand's grip frame, in the hand's own space, and its scale. */
  let grip = null;

  /** Points one bone along a direction in the body's frame, fully, at rest. */
  function pointAtRest(name, child, direction) {
    const bone = bones[name];
    const next = bones[child];
    if (!bone || !next) return false;
    bone.getWorldPosition(scratch.a);
    next.getWorldPosition(scratch.b);
    const now = scratch.b.sub(scratch.a).normalize();
    const want = scratch.d.set(direction[0], direction[1], direction[2]).normalize().applyQuaternion(scratch.root);
    scratch.full.setFromUnitVectors(now, want);
    bone.getWorldQuaternion(scratch.q);
    const world = scratch.full.multiply(scratch.q);
    bone.parent.getWorldQuaternion(scratch.p);
    bone.quaternion.copy(scratch.p.invert().multiply(world));
    bone.updateMatrixWorld(true);
    return true;
  }

  /** Works the holds and the grip out once, off the bind pose. */
  function measureHolds() {
    root.updateMatrixWorld(true);
    root.getWorldQuaternion(scratch.root);
    const arm = Object.values(bones).filter((bone) => /(Arm|ForeArm|Hand)$/.test(bareBone(bone.name)));
    const bind = new Map(arm.map((bone) => [bone, bone.quaternion.clone()]));

    // The grip frame, while the arm is still in the T.
    const hand = bones.RightHand;
    const fore = bones.RightForeArm;
    if (hand && fore) {
      const toLocal = hand.getWorldQuaternion(new THREE.Quaternion()).invert();
      const fingers = hand.getWorldPosition(new THREE.Vector3()).sub(fore.getWorldPosition(new THREE.Vector3())).normalize();
      const palm = new THREE.Vector3(0, -1, 0).applyQuaternion(scratch.root);
      palm.addScaledVector(fingers, -palm.dot(fingers)).normalize();
      const thumb = new THREE.Vector3().crossVectors(fingers, palm).normalize();
      grip = {
        fingers: fingers.applyQuaternion(toLocal).normalize(),
        palm: palm.applyQuaternion(toLocal).normalize(),
        thumb: thumb.applyQuaternion(toLocal).normalize(),
        unit: hand.getWorldScale(new THREE.Vector3()).x || 1,
      };
    }

    for (const [kind, row] of Object.entries(HOLDS)) {
      holdTargets[kind] = [];
      for (const side of row.sides) {
        const mirror = side === 'Left' ? 1 : -1;
        const upper = [row.upper[0] * mirror, row.upper[1], row.upper[2]];
        const forearm = [row.fore[0] * mirror, row.fore[1], row.fore[2]];
        if (pointAtRest(`${side}Arm`, `${side}ForeArm`, upper)) {
          holdTargets[kind].push({ bone: bones[`${side}Arm`], to: bones[`${side}Arm`].quaternion.clone(), share: 1 - row.swing });
        }
        if (pointAtRest(`${side}ForeArm`, `${side}Hand`, forearm)) {
          holdTargets[kind].push({ bone: bones[`${side}ForeArm`], to: bones[`${side}ForeArm`].quaternion.clone(), share: 1 });
        }
        // The wrist held straight, which is the bind's.
        const wrist = bones[`${side}Hand`];
        if (wrist) holdTargets[kind].push({ bone: wrist, to: bind.get(wrist).clone(), share: 1 });
      }
      for (const [bone, q] of bind) bone.quaternion.copy(q);
      root.updateMatrixWorld(true);
    }
  }

  /**
   * What the clip gave each held bone this frame, before it was held.
   *
   * The mixer only writes a bone whose value has changed since the frame
   * before, so a bone left held is a bone it may not write again: next frame
   * the hold would start from last frame's hold rather than from the clip, and
   * on alternate frames it did, which was the hand flicking two centimetres
   * back and forth. So the clip's value is put back before the mixer runs,
   * and the hold always starts from what the clip says.
   */
  const heldFrom = new Map();

  function unhold() {
    for (const [bone, q] of heldFrom) bone.quaternion.copy(q);
    heldFrom.clear();
  }

  /**
   * Straight into the idle at full weight, and the bones set from it now. A
   * body that has just been dressed, or put back on its feet, is otherwise in
   * its bind until the next frame's clips reach it — and the bind is a T.
   */
  function stand() {
    const idle = actionsFor('idle');
    if (!idle) return;
    fade(idle.lower, 1, FADE);
    fade(idle.upper, 1, FADE);
    const at = idlePhase * idle.lower.action.getClip().duration;
    idle.lower.action.time = at;
    idle.upper.action.time = at;
    mixer.update(0);
  }

  /** Holds the arm bones toward a hold's rotations, by `weight`. */
  function hold(kind, weight) {
    for (const target of holdTargets[kind] ?? []) {
      if (!heldFrom.has(target.bone)) heldFrom.set(target.bone, target.bone.quaternion.clone());
      target.bone.quaternion.slerp(target.to, weight * target.share);
    }
  }

  /** The thing in the right hand, drawn from what the reading says it is, in the grip. */
  function drawItem(held, weight) {
    const hand = bones.RightHand;
    if (!hand || !grip) return;
    const show = Boolean(held) && weight > 0.5;
    if (!show) {
      if (item) item.visible = false;
      return;
    }
    const shape = ['box', 'cylinder', 'sphere'].includes(held.shape) ? held.shape : 'box';
    // The thing itself where it has a model (`props.js`), at its size and
    // centred on its middle; the shape it was given where it has none.
    const key = `${held.kind ?? shape}|${held.width}|${held.height}`;
    if (!item || itemKey !== key) {
      if (item) {
        item.removeFromParent();
        item.geometry.dispose();
      }
      const model = held.kind ? propGeometry(held.kind, held.width, held.height, held.colour) : null;
      if (model) model.translate(0, -held.height / 2, 0);
      const geometry =
        model ??
        (shape === 'sphere'
          ? new THREE.SphereGeometry(0.5, 12, 8)
          : shape === 'cylinder'
            ? new THREE.CylinderGeometry(0.5, 0.5, 1, 16)
            : new THREE.BoxGeometry(1, 1, 1));
      item = new THREE.Mesh(geometry, model ? propMaterials() : keep(new THREE.MeshStandardMaterial({ roughness: 0.7, metalness: 0 })));
      item.userData.modelled = Boolean(model);
      item.castShadow = true;
      item.name = 'held';
      hand.add(item);
      itemShape = shape;
      itemKey = key;
    }
    const { fingers, palm, thumb, unit } = grip;
    const width = held.width;
    const height = held.height;
    const flat = shape !== 'sphere' && height < width * 0.5;
    // Which of the thing's own axes lies along which of the hand's.
    let x = fingers;
    let y = shape === 'sphere' ? palm : flat ? palm : thumb;
    let z = new THREE.Vector3().crossVectors(x, y);
    scratch.basis.makeBasis(x, y, z);
    item.quaternion.setFromRotationMatrix(scratch.basis);
    // How far off the palm its middle is: half of whatever lies across the palm.
    const off = (shape === 'sphere' ? width / 2 : flat ? height / 2 : width / 2) + GRIP.gap;
    const along = GRIP.reach + (flat ? width * 0.25 : 0);
    item.position
      .set(0, 0, 0)
      .addScaledVector(fingers, along / unit)
      .addScaledVector(palm, off / unit);
    if (item.userData.modelled) item.scale.setScalar(1 / unit);
    else {
      item.scale.set(width / unit, height / unit, width / unit);
      item.material.color.set(held.colour ?? 0xcccccc);
    }
    item.visible = true;
  }

  /** How much of the walk stepping in place takes, turning at `turn` rad/s. */
  function pivot(turn) {
    const rate = Math.abs(turn);
    if (rate < PIVOT.from) return 0;
    return Math.min(PIVOT.most, ((rate - PIVOT.from) / (PIVOT.full - PIVOT.from)) * PIVOT.most);
  }

  /**
   * Leaning: forward into setting off and back into pulling up, from how the
   * body's speed is changing, and banked into a turn by how fast it is turning
   * at speed. The whole drawn body tips about its feet. Eased both ways, so
   * the capsule reaching speed in a twelfth of a second is a lean and not a
   * jolt.
   */
  function leanInto(reading, dt, still) {
    const speed = Math.max(0, reading.speed ?? 0);
    const free = !reading.climb || reading.climb === 'free';
    const grounded = reading.grounded !== false && free && !reading.seated && !reading.cart && !reading.hidden && !(reading.knocked > 0);
    const raw = dt > 0 ? (speed - lastSpeed) / dt : 0;
    lastSpeed = speed;
    accel += (raw - accel) * Math.min(1, dt * LEAN.accelEase);
    const wantLean = grounded ? Math.max(-LEAN.most, Math.min(LEAN.most, accel * LEAN.perAccel)) : 0;
    const wantBank = grounded && !still ? Math.max(-LEAN.bank, Math.min(LEAN.bank, -(reading.turn ?? 0) * speed * LEAN.perTurn)) : 0;
    const ease = Math.min(1, dt * LEAN.ease);
    lean += (wantLean - lean) * ease;
    bank += (wantBank - bank) * ease;
    body.rotation.x = lean;
    body.rotation.z = bank;
  }

  /** The pieces a look hung on the bones (`dress.js`). */
  const dressed = [];

  /**
   * A build, as scales on the bones, drawn only: the height on the whole body
   * about its feet; the chest, the belly and the hips across (never along
   * the spine), each undone on the bones above it so a broad chest does not
   * also mean a broad head; and the shoulders as the length of the collar
   * bone. Set once: `unpin` took the tracks out that would have written over
   * it. The capsule, and everything that measures anybody, is untouched.
   */
  function shape(build) {
    if (!build) return;
    const across = (bone, k) => bone && bone.scale.set(k, 1, k);
    /*
     * A child under a parent scaled across is scaled across too, in the
     * parent's frame; undoing that in the child's own frame is the parent's
     * inverse seen through the child's bind rotation (only its diagonal is
     * kept: the bones are close to square with their parents at rest).
     */
    const undo = (child, k) => {
      if (!child) return;
      const r = new THREE.Matrix4().makeRotationFromQuaternion(child.quaternion);
      const m = new THREE.Matrix4().copy(r).transpose().multiply(new THREE.Matrix4().makeScale(1 / k, 1, 1 / k)).multiply(r);
      const e = m.elements;
      child.scale.set(child.scale.x * e[0], child.scale.y * e[5], child.scale.z * e[10]);
    };
    body.scale.setScalar(build.height ?? 1);
    const hips = build.hips ?? 1;
    const belly = build.belly ?? 1;
    const chest = build.chest ?? 1;
    across(bones.Hips, hips);
    if (bones.Spine) bones.Spine.scale.set(1 / hips, 1, 1 / hips);
    across(bones.Spine1, belly);
    if (bones.Spine2) bones.Spine2.scale.set(chest / belly, 1, chest / belly);
    undo(bones.Neck, chest);
    undo(bones.LeftShoulder, chest);
    undo(bones.RightShoulder, chest);
    for (const side of ['Left', 'Right']) {
      const arm = bones[`${side}Arm`];
      if (arm) arm.position.multiplyScalar(build.shoulders ?? 1);
    }
  }

  const figure = {
    /** Puts the body on, once the file is here. */
    wear() {
      if (!asset || body) return;
      body = cloneSkinned(asset.scene);
      materials = {
        cloth: keep(new THREE.MeshStandardMaterial({ color: tint.cloth, roughness: 0.85, metalness: 0 })),
        skin: keep(new THREE.MeshStandardMaterial({ color: tint.skin, roughness: 0.6, metalness: 0 })),
        hair: keep(new THREE.MeshStandardMaterial({ color: tint.hair, roughness: 0.7, metalness: 0 })),
        shoe: keep(new THREE.MeshStandardMaterial({ color: SHOE, roughness: 0.4, metalness: 0 })),
      };
      body.traverse((node) => {
        if (node.isBone) bones[bareBone(node.name)] = node;
        if (!node.isSkinnedMesh) return;
        const region = String(node.material?.name ?? '').replace('figure_', '') || 'cloth';
        if (node.material?.map) for (const m of Object.values(materials)) m.map ??= node.material.map;
        node.material = materials[region] ?? materials.cloth;
        node.castShadow = true;
        node.receiveShadow = true;
        // Round the whole person whatever the clip: the file's own bounds are
        // the rest pose, and a raised arm is outside them.
        node.geometry.computeBoundingSphere();
        const sphere = node.geometry.boundingSphere.clone();
        sphere.radius *= 1.6;
        node.boundingSphere = sphere;
        meshes.push(node);
      });
      if (style) {
        // A whole look (`appearance.js`): the clothes in their zones, and the
        // skin and hair it says, whatever the key would have picked.
        tint.skin.set(style.skin);
        tint.hair.set(style.hair.style === 'bald' ? style.skin : style.hair.colour);
        materials.skin.color.copy(tint.skin);
        materials.hair.color.copy(tint.hair);
        const zoned = keep(clothMaterial(materials.cloth, style.outfit, style.skin, asset.body));
        for (const node of meshes) if (node.material === materials.cloth) node.material = zoned;
        materials.cloth = zoned;
      }
      for (const m of Object.values(materials)) m.needsUpdate = true;
      mixer = new THREE.AnimationMixer(body);
      root.add(body);
      measureHolds();
      if (style) {
        shape(style.build);
        const skeleton = meshes[0]?.skeleton;
        const bindOf = (name) => {
          const at = skeleton ? skeleton.bones.indexOf(bones[name]) : -1;
          return at >= 0 ? skeleton.boneInverses[at] : null;
        };
        for (const mesh of dressPieces({ look: style, m: asset.body, bones, bindOf })) {
          keep(mesh.geometry);
          dressed.push(mesh);
        }
      }
      stand();
    },

    /**
     * One frame: the state's clips, faded toward, at the right speed.
     *
     * `state` is what `gait.js` read; `reading` is the plain body it read.
     */
    animate(state, reading, dt) {
      if (!mixer) return playing;
      const want = clipsFor(state, reading, asset.speeds, asset.gaits);
      playing = want;

      // The throw: held at the wind-up while the key is held, then let go.
      const windup = want.upper === 'throw' ? want.windup : 0;
      if (lastWindup > 0 && windup === 0) released = true;
      lastWindup = windup;
      const throwing = actions.throw?.upper;
      if (released && throwing && throwing.weight > 0 && throwing.action.time >= throwing.action.getClip().duration - 1e-3) {
        released = false;
      }

      /*
       * What the legs want, as weights that add up to one: a blend over the
       * walk and the run while moving, the state's own clip otherwise. Stepping
       * in place while standing and turning borrows the walk (see `pivot`).
       */
      const gaits = asset.gaits;
      const lower = {};
      const walking = (actions.walk?.lower.weight ?? 0) + (actions.run?.lower.weight ?? 0);
      const standing = want.base === 'idle' || want.base === 'talk';
      const turning = standing && !want.blend ? pivot(reading.turn ?? 0) : 0;
      if (want.blend) {
        lower.walk = want.blend.walk;
        lower.run = want.blend.run;
        // From standing, the first frame of the stride is the feet passing,
        // which is a step and not a pose snapping from one leg to the other.
        if (walking < 0.05) phase = gaits.walk?.rest?.[0] ?? 0.25;
        phase = (phase + want.blend.cycle * dt) % 1;
        lastCycle = want.blend.cycle;
      } else if (turning > 0) {
        lower.walk = turning;
        lower[want.base] = 1 - turning;
        if (walking < 0.05) phase = gaits.walk?.rest?.[0] ?? 0.25;
        phase = (phase + Math.abs(reading.turn ?? 0) * PIVOT.cycle * dt) % 1;
      } else {
        lower[want.base] = 1;
        // Stopping: the stride is played on to the next time the feet pass,
        // so the body finishes its step and settles, rather than freezing
        // with a foot in the air while the idle fades over it.
        if (walking > 0.02 && gaits.walk) {
          const target = nextRest(phase, gaits.walk.rest);
          const step = Math.max(lastCycle, SETTLE) * dt;
          const gap = (target - phase + 1) % 1;
          phase = gap <= step ? target % 1 : (phase + step) % 1;
        }
      }
      const upperWants = want.upper ? { [want.upper]: 1 } : released ? { throw: 1 } : lower;

      /*
       * A body playing nothing at all — just dressed, or put back on its feet
       * by `reset()` — starts its clip at full weight. Fading in from nothing
       * is fading in from the bind, and the bind is a T: an NPC coming on shift
       * stood with its arms straight out for a fifth of a second.
       */
      const cold = !Object.values(actions).some((pair) => pair.lower.weight > 0 || pair.upper.weight > 0);
      const fadeBy = cold ? FADE : dt;
      for (const name of new Set([...Object.keys(lower), ...Object.keys(upperWants), ...Object.keys(actions)])) {
        const pair = actionsFor(name);
        if (!pair) continue;
        // A layer joining its clip starts where the other layer already is.
        const sync = pair.lower.weight > 0 ? pair.lower.action.time : pair.upper.weight > 0 ? pair.upper.action.time : null;
        fade(pair.lower, lower[name] ?? 0, fadeBy, sync);
        fade(pair.upper, upperWants[name] ?? 0, fadeBy, sync);
        const cycle = gaits[name];
        if (cycle && (pair.lower.weight > 0 || pair.upper.weight > 0)) {
          // In step: both layers of the walk and the run from the one phase.
          const time = ((phase + cycle.offset) % 1) * cycle.duration;
          pair.lower.action.timeScale = 0;
          pair.lower.action.time = time;
          if (upperWants === lower) {
            pair.upper.action.timeScale = 0;
            pair.upper.action.time = time;
          }
        } else if (lower[name]) {
          const rate = name === 'idle' || name === 'talk' ? want.timeScale * idleRate : want.timeScale;
          pair.lower.action.timeScale = rate;
          if (upperWants === lower) pair.upper.action.timeScale = rate;
        }
      }
      if (want.upper) {
        const upper = actions[want.upper].upper.action;
        if (want.upper === 'throw') {
          upper.timeScale = 0;
          upper.time = Math.min(1, windup) * WINDUP_REACH * upper.getClip().duration;
        } else {
          upper.timeScale = want.upperScale;
        }
      } else if (released && actions.throw) {
        actions.throw.upper.action.timeScale = 1;
      }
      // Down onto the seat, as far as sitting is what is playing.
      const sitting = actions.sit?.lower.weight ?? 0;
      body.position.y = asset.sitHips > 0 ? sitting * (ON_SEAT - asset.sitHips) : 0;
      leanInto(reading, dt, standing && !want.blend);
      unhold();
      mixer.update(dt);

      // Carrying: the arms held over what the clip just did, faded in and out.
      const step = FADE > 0 ? dt / FADE : 1;
      for (const kind of ['one', 'two']) {
        const wanted = want.hold === kind ? 1 : 0;
        holding[kind] = wanted > holding[kind] ? Math.min(wanted, holding[kind] + step) : Math.max(wanted, holding[kind] - step);
      }
      if (holding.two > 0) hold('two', holding.two);
      if (holding.one > 0) hold('one', holding.one);
      drawItem(reading.held ?? null, holding.one);
      return playing;
    },

    /** What it is playing: the clips, and each running action's weight and time. */
    read() {
      const running = {};
      for (const [name, pair] of Object.entries(actions)) {
        if (pair.lower.weight > 0 || pair.upper.weight > 0) {
          running[name] = {
            lower: Math.round(pair.lower.weight * 100) / 100,
            upper: Math.round(pair.upper.weight * 100) / 100,
            time: Math.round((pair.lower.weight > 0 ? pair.lower.action.time : pair.upper.action.time) * 1000) / 1000,
            timeScale: Math.round(pair.lower.action.timeScale * 1000) / 1000,
          };
        }
      }
      return {
        ...playing,
        running,
        holding: { one: Math.round(holding.one * 100) / 100, two: Math.round(holding.two * 100) / 100 },
        phase: Math.round(phase * 1000) / 1000,
        lean: Math.round(lean * 1000) / 1000,
        bank: Math.round(bank * 1000) / 1000,
        item: item?.visible ? itemShape : null,
      };
    },

    /** Back to standing, all at once. */
    reset() {
      if (!mixer) return;
      mixer.stopAllAction();
      for (const pair of Object.values(actions)) {
        pair.lower.weight = 0;
        pair.upper.weight = 0;
      }
      released = false;
      lastWindup = 0;
      phase = 0;
      lastCycle = 0;
      lean = 0;
      bank = 0;
      lastSpeed = 0;
      accel = 0;
      if (body) body.rotation.set(0, 0, 0);
      holding.one = 0;
      holding.two = 0;
      unhold();
      stand();
      if (item) item.visible = false;
    },
  };

  if (asset) figure.wear();
  else {
    waiting.add(figure);
    load();
  }

  return {
    object3D: root,
    animate: (state, reading, dt) => figure.animate(state, reading, dt),
    read: () => figure.read(),
    reset: () => figure.reset(),
    /** What you have got on. */
    setColor: (next) => {
      tint.cloth.set(next);
      if (materials) materials.cloth.color.copy(tint.cloth);
    },
    /** The preset's key, which picks the skin and hair the same way `look` does. */
    setAccent: (next) => setLook(next),
    setLook,
    /** Whether the body is on yet. */
    get worn() {
      return meshes.length > 0;
    },
    /** The skinned parts and the bones, by their own names, for a check. */
    get meshes() {
      return meshes;
    },
    get bones() {
      return bones;
    },
    dispose() {
      waiting.delete(figure);
      mixer?.stopAllAction();
      if (body) mixer?.uncacheRoot(body);
      root.removeFromParent();
      item?.geometry.dispose();
      for (const thing of disposables) thing.dispose?.();
    },
  };
}
